import { type CSSProperties, useEffect, useRef, useCallback, useState } from 'react';
import {
  GripVertical,
  Pencil,
  Trash2,
  MoreVertical,
  Plus,
  UserRound,
  ThumbsUp,
  ListChecks,
  CheckCircle2,
  CircleDot,
  Undo2,
  Eye,
  EyeOff,
  ArrowRightLeft,
  FolderKanban,
  FolderInput,
  ChevronRight,
  ChevronDown,
  ListPlus,
} from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import type { TodoItemResponse, TodoItemStatus } from '@/lib/api';
import { ProjectChip } from './ProjectChip';
import { SectionPathChip } from './SectionPathChip';
import { getCompletionBlockers, completionBlockedMessage } from '../lib/completionGate';
import { BulkActionsMenuItems } from './BulkActionsMenuItems';
import { resolveProjectColor } from '../lib/projectColors';
import { useTodoListContext } from '../context';
import { formatItemId } from '../lib/itemId';
import { useSectionCollapse } from '../lib/collapsedSections';
// Shared status vocabulary (badge styles + the "Set status" options), also used
// by the personal "My Tasks" board so the two surfaces stay in lockstep.
import { STATUS_STYLES, WORK_STATUSES, CANCEL_STATUS, COMPLETE_STATUS } from '../lib/status';

interface TodoItemRowProps {
  item: TodoItemResponse;
  /** Override for canEdit — when omitted, falls back to isOwner from context */
  canEdit?: boolean;
  /**
   * Whether the user can manage the section this row lives in — reorder items and add
   * items below. Managers always can; a member can for a section assigned to them.
   * Omitted → falls back to isOwner (manager only).
   */
  canManageSection?: boolean;
  /** Which grouping this row renders under — drives whether the meta chip shows
   *  the project (section view) or the section path (project view). */
  groupContext?: 'section' | 'project';
  /**
   * Suppress the drag affordance entirely: no grip handle and the row is not
   * sortable, even for a manager. Used by the Projects view, which is a
   * read-only grouping lens (reorder lives in the Sections view; a task's
   * project is changed from its edit dialog).
   */
  dragDisabled?: boolean;
  /**
   * Whether this row's section is (effectively) hidden from the public view —
   * either the section itself or an ancestor is hidden. When true the per-item
   * eye is moot (the section gate already hides the task), so it renders as a
   * disabled indicator rather than a toggle. Only meaningful on a public list.
   */
  sectionHidden?: boolean;
  /**
   * Render this row as a sub-task: indented under its parent, no drag handle
   * (sub-tasks are locked to their parent on the shared list — not draggable),
   * no further nesting, and its "+S" makes a SIBLING (same parent). Set by the
   * parent row when it renders its children.
   */
  asSubtask?: boolean;
  /**
   * The parent row's section id, passed down when rendering a sub-task so its
   * section-path chip (project/list view) matches the parent. Sub-tasks have no
   * section of their own, so without this they'd read as "Unsorted". Only
   * meaningful with {@link asSubtask}.
   */
  parentSectionId?: string | null;
}

export function TodoItemRow({
  item,
  canEdit,
  canManageSection,
  groupContext = 'section',
  dragDisabled = false,
  sectionHidden = false,
  asSubtask = false,
  parentSectionId = null,
}: TodoItemRowProps) {
  const {
    isOwner,
    isListPublic,
    reviewMode,
    onToggleItem: onToggle,
    onApproveItem: onApprove,
    onRejectItem: onReject,
    onEditItem: onEdit,
    onDeleteItem: onDelete,
    onMoveItem: onMove,
    onToggleItemHidden,
    onSetItemStatus,
    itemKeyPrefix,
    onUpdateItemTitle: onUpdateTitle,
    onCreateItemBelow,
    onAssignItem,
    onUnassignItem,
    assignableMembers,
    resolvePrincipalName,
    unreadAssignedItemIds,
    onViewAssignment,
    unreadReviewItemIds,
    onViewReview,
    onRequestReview,
    reviewerTargets,
    isListReviewer,
    projectById,
    projects,
    onSetItemProject,
    movableSectionOptions,
    canMoveToUnsorted,
    onSetItemSection,
    showProjectOnTasks = true,
    showSectionPathOnTasks = true,
    resolveSectionPath,
    subtasksByParent,
    onCreateSubtask,
    selectedItemIds,
    selectionActive,
    onItemSelect,
    selectionCount,
    bulkActions,
  } = useTodoListContext();
  const project = item.projectId ? projectById?.get(item.projectId) : undefined;
  // In the project view, the useful context is the task's section path; in the
  // section view, it's the project. Each is gated by a per-list display toggle.
  const inProjectView = groupContext === 'project';
  // A sub-task has no section of its own — resolve the chip from the parent's
  // section so it matches the parent row instead of reading "Unsorted".
  const chipSectionId = asSubtask ? parentSectionId : item.sectionId;
  const sectionPath = inProjectView ? (resolveSectionPath?.(chipSectionId) ?? []) : [];

  const isUnreadAssignment = unreadAssignedItemIds?.has(item.itemId) ?? false;
  const isUnreadReview = unreadReviewItemIds?.has(item.itemId) ?? false;
  // A designated reviewer gets the review affordances (Verify / Send-back + the
  // actions menu, where they can annotate + add sub-tasks) ONLY while the item is
  // in review — outside that window they're a plain member. Verifying is a
  // reviewer/manager act; the assignee (doer) cannot approve their own work.
  const canReview = (isListReviewer ?? false) && item.status === 'in_review';
  const effectiveCanEdit = canEdit ?? isOwner;
  // Multi-select: only top-level editable tasks are selectable (sub-tasks are
  // inert/nested). `isSelected` drives the highlight; when a selection is active a
  // plain click on a selectable row extends it (see the row onClick below).
  const selectable = !asSubtask && !!onItemSelect && effectiveCanEdit;
  const isSelected = !asSubtask && (selectedItemIds?.has(item.itemId) ?? false);
  // When THIS row is part of a multi-selection, its ⋯ menu acts on the WHOLE
  // selection (Finder-style) — the bulk verbs replace the single-item ones and
  // Edit is dropped. A single selection (or an unselected row) keeps the normal
  // per-item menu, so a ⋯ always does what the highlight implies.
  const inBulkMode = isSelected && (selectionCount ?? 0) > 1 && !!bulkActions;
  // Assignees get the work-states; only managers can additionally cancel. On a
  // review-required list, managers also get a "Completed" shortcut here — their
  // sanctioned bypass of the review step (everyone else goes Send-for-review →
  // Verify). Outside review mode, completion is the checkbox, so it's omitted.
  const statusOptions = isOwner
    ? reviewMode
      ? [...WORK_STATUSES, COMPLETE_STATUS, CANCEL_STATUS]
      : [...WORK_STATUSES, CANCEL_STATUS]
    : WORK_STATUSES;
  const effectiveCanManageSection = canManageSection ?? isOwner;
  // Public-visibility affordances. The eye is owner-only chrome that only makes
  // sense on a public list. `effectivelyHidden` drives the dimmed "won't be
  // public" cue — set when the task itself is hidden OR its section gate is.
  const showVisibilityEye = !!isListPublic && isOwner;
  const effectivelyHidden = !!isListPublic && (item.hiddenFromPublic || sectionHidden);
  const isCompleted = item.status === 'completed';
  const isInReview = item.status === 'in_review';
  // In a review-required list the checkbox is the doer's "done" marker for
  // TOP-LEVEL tasks (the "I've finished my part" flag), ORTHOGONAL to status:
  // checking it toggles `done`, not the status. A task must be `done` to be sent
  // for review; "Send for review" then puts it In Review, and a reviewer's Verify
  // is the only path to Completed. SUB-TASKS aren't reviewable, so their checkbox
  // is the classic completion toggle even here. Outside review mode everything
  // toggles completion.
  const reviewProgressCheckbox = reviewMode && !item.parentItemId;
  const isChecked = reviewProgressCheckbox ? item.done : isCompleted;
  const statusStyle = STATUS_STYLES[item.status] ?? STATUS_STYLES.pending;
  const { toast } = useToast();

  // Block checking a task off while it still has open sub-tasks / checklist
  // items — whether that check COMPLETES it (normal list) or marks it "done" (a
  // review list; a parent isn't "done" with pending sub-tasks). Un-checking is
  // always allowed.
  const handleToggle = () => {
    if (!isChecked) {
      const blockers = getCompletionBlockers(item);
      if (blockers.blocked) {
        toast({ title: 'Finish sub-items first', description: completionBlockedMessage(blockers) });
        return;
      }
    }
    onToggle?.(item);
  };
  const titleRef = useRef<HTMLDivElement>(null);
  const addBelowRef = useRef<HTMLInputElement>(null);
  const [addBelowActive, setAddBelowActive] = useState(false);
  const [addBelowTitle, setAddBelowTitle] = useState('');
  // Reveal the row's action buttons on tap (mobile — no hover). Desktop reveals
  // them on hover via `group-hover`. A tap toggles `rowActive`; a pointer-down
  // anywhere OUTSIDE this row clears it — which also covers "tap another row"
  // (that row is outside this one, so this row closes while the other opens),
  // giving one-active-at-a-time without hoisting the state to every parent.
  const rowRef = useRef<HTMLDivElement>(null);
  const [rowActive, setRowActive] = useState(false);
  useEffect(() => {
    if (!rowActive) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (rowRef.current && !rowRef.current.contains(e.target as Node)) {
        setRowActive(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [rowActive]);
  const [subtaskAddActive, setSubtaskAddActive] = useState(false);
  const [subtaskAddTitle, setSubtaskAddTitle] = useState('');
  const subtaskAddRef = useRef<HTMLInputElement>(null);

  // Sub-tasks nested under this row (always empty for a sub-task — one level only).
  const childSubtasks = asSubtask ? [] : (subtasksByParent?.get(item.itemId) ?? []);
  const hasSubtasks = childSubtasks.length > 0;
  const subtaskCollapse = useSectionCollapse(`subtasks:${item.itemId}`);
  // "+S" target: a sub-task makes a SIBLING (its own parent); a top-level task
  // makes a child. One level deep — enforced server-side too.
  const subtaskParentId = asSubtask ? item.parentItemId : item.itemId;
  const canAddSubtask = !!onCreateSubtask && effectiveCanEdit && !!subtaskParentId;
  // Shared reveal behavior for the row's quick-add buttons (+ and +S): hidden by
  // default, shown on hover (desktop) or once the row is tapped (mobile).
  const actionVisibility = `${
    rowActive ? 'opacity-100' : 'opacity-0'
  } transition-opacity group-hover:opacity-100 focus-visible:opacity-100`;

  // Review readiness (computed from the counts already on the row). A parent can
  // only be sent for review once every sub-task is done/cancelled AND every
  // checklist item is checked; sub-tasks are never independently reviewable. The
  // server enforces the same — this just disables the action with a reason.
  const reviewIncompleteSubtasks = (item.subtaskTotal ?? 0) > (item.subtaskDone ?? 0);
  const reviewIncompleteChecklist = (item.checklistTotal ?? 0) > (item.checklistDone ?? 0);
  // Only a task the doer has marked done (the checkbox) can be sent for review —
  // on top of the existing sub-task/checklist-completeness gate.
  const reviewNotDone = !item.done;
  const reviewBlocked = reviewNotDone || reviewIncompleteSubtasks || reviewIncompleteChecklist;
  const reviewBlockReason = reviewNotDone
    ? 'Check the box to mark it done first'
    : reviewIncompleteSubtasks && reviewIncompleteChecklist
      ? 'Finish every sub-task and check off every checklist item first'
      : reviewIncompleteSubtasks
        ? 'Finish or cancel every sub-task first'
        : 'Check off every checklist item first';

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.itemId,
    data: { type: 'item' },
    // Sub-tasks are locked to their parent on the shared list — never draggable;
    // the Projects view disables drag for every row (read-only grouping lens).
    disabled: !effectiveCanManageSection || asSubtask || dragDisabled,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const saveTitle = useCallback(() => {
    if (!titleRef.current) return;
    const newTitle = titleRef.current.innerText?.trim() ?? '';
    if (newTitle && newTitle !== item.title) {
      onUpdateTitle?.(item.itemId, newTitle);
    } else if (!newTitle) {
      titleRef.current.innerText = item.title;
    }
  }, [item.itemId, item.title, onUpdateTitle]);

  return (
    <div ref={setNodeRef} style={style} data-todo-row>
      <div
        ref={rowRef}
        className={cn(
          'group flex items-start gap-2 rounded-md px-3 py-2 transition-colors',
          isSelected ? 'bg-primary/10 ring-1 ring-primary/40' : 'hover:bg-muted/50',
          selectionActive && selectable ? 'cursor-pointer select-none' : '',
        )}
        onClick={(e) => {
          // Selection model (desktop, standard multi-select):
          //  • a plain click on the ROW BODY selects just this task (click once,
          //    then shift/cmd-click to add more)
          //  • a plain click on the TITLE TEXT edits it inline (rename)
          //  • a modifier click always extends the selection — shift = range,
          //    cmd/ctrl = toggle — even on the title
          //  • clicks on interactive controls (checkbox / buttons / menu) do their
          //    own thing
          const target = e.target as HTMLElement;
          const onInteractive = !!target.closest(
            'button, input, a, [role="button"], [role="checkbox"], [role="menuitem"]',
          );
          const onTitle = !!target.closest('[contenteditable="true"]');
          const isModifier = e.shiftKey || e.metaKey || e.ctrlKey;
          if (selectable && onItemSelect && !onInteractive && !(onTitle && !isModifier)) {
            e.preventDefault();
            onItemSelect(item.itemId, {
              shiftKey: e.shiftKey,
              metaKey: e.metaKey,
              ctrlKey: e.ctrlKey,
            });
            // Selecting the item counts as "seeing it" — clear its unread dots.
            if (isUnreadAssignment) onViewAssignment?.('item', item.itemId);
            if (isUnreadReview) onViewReview?.(item.itemId);
            return;
          }
          // Title inline-edit (plain click on the text) or a non-selectable row:
          // tapping the row toggles its quick-add buttons (mobile — no hover);
          // tapping again hides them.
          setRowActive((prev) => !prev);
          if (isUnreadAssignment) onViewAssignment?.('item', item.itemId);
          if (isUnreadReview) onViewReview?.(item.itemId);
        }}
        onDoubleClick={(e) => {
          // Double-click opens the details/edit panel — but let double-clicks on
          // the interactive controls (checkbox, buttons, the inline-editable
          // title) keep their own behavior.
          if (
            (e.target as HTMLElement).closest(
              'button, input, a, [contenteditable="true"], [role="button"], [role="checkbox"], [role="menuitem"]',
            )
          )
            return;
          if (effectiveCanEdit || canReview) onEdit?.(item);
        }}
      >
        {effectiveCanManageSection && !asSubtask && !dragDisabled && (
          <button
            type="button"
            className="mt-0.5 cursor-grab touch-none text-muted-foreground/50 hover:text-muted-foreground"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}

        {effectiveCanEdit ? (
          <Checkbox
            checked={isChecked}
            onCheckedChange={handleToggle}
            className="mt-0.5"
            aria-label={
              reviewProgressCheckbox
                ? isChecked
                  ? `Mark "${item.title}" as not done`
                  : `Mark "${item.title}" as done`
                : `Mark "${item.title}" as ${isChecked ? 'pending' : 'completed'}`
            }
          />
        ) : !isOwner ? (
          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-white flex-shrink-0" />
        ) : null}

        <div className={`flex-1 min-w-0 ${effectivelyHidden ? 'opacity-50' : ''}`}>
          {/* items-start (not center) so the ID + caret stay at the TOP next to the
              checkbox on a multi-line title, rather than centering down the middle. */}
          <div className="flex items-start gap-2">
            {itemKeyPrefix && (
              <code
                className="mt-0.5 text-[10px] font-mono text-muted-foreground flex-shrink-0"
                title="Task id"
              >
                {formatItemId(itemKeyPrefix, item.seq)}
              </code>
            )}
            {/* Collapse/expand caret — sits BETWEEN the ID and the name so it isn't a
                touch target immediately next to the checkbox on mobile. The chevron
                shows only on a top-level task that HAS sub-tasks; every other row
                (including sub-tasks) gets an equal-width spacer so ids + names stay
                aligned. */}
            {!asSubtask && hasSubtasks ? (
              <button
                type="button"
                className="mt-0.5 flex-shrink-0 text-muted-foreground/60 hover:text-muted-foreground"
                aria-label={
                  subtaskCollapse.expanded
                    ? `Collapse sub-tasks of "${item.title}"`
                    : `Expand sub-tasks of "${item.title}"`
                }
                onClick={(e) => {
                  e.stopPropagation();
                  subtaskCollapse.toggle();
                }}
              >
                {subtaskCollapse.expanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : (
              <span className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            )}
            {isUnreadAssignment && (
              <span
                className="h-2 w-2 rounded-full bg-red-500 flex-shrink-0"
                title="New assignment — click to dismiss"
                aria-label="New assignment"
              />
            )}
            {isUnreadReview && (
              <span
                className="h-2 w-2 rounded-full bg-amber-500 flex-shrink-0"
                title="Review requested — click to dismiss"
                aria-label="Review requested"
              />
            )}
            {/* Title + metadata + review actions share ONE wrapping block. The
                badge cluster and the review-action row sit next to the title
                when there's room and drop onto their own line beneath it when
                there isn't — driven by available WIDTH (not a viewport
                breakpoint), so it self-corrects both on mobile and on
                width-starved in-review rows on desktop. */}
            <div className="flex flex-wrap items-start gap-x-2 gap-y-1 flex-1 min-w-0">
              <div
                ref={titleRef}
                contentEditable={effectiveCanEdit}
                suppressContentEditableWarning
                className={`text-sm outline-none whitespace-pre-wrap break-words min-w-0 ${
                  isCompleted ? 'line-through text-muted-foreground' : 'text-foreground'
                } ${effectiveCanEdit ? 'cursor-text focus:ring-1 focus:ring-primary/30 focus:rounded-sm focus:px-1 focus:-mx-1' : ''}`}
                onBlur={saveTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    (e.target as HTMLElement).blur();
                  }
                  if (e.key === 'Escape') {
                    if (titleRef.current) titleRef.current.innerText = item.title;
                    (e.target as HTMLElement).blur();
                  }
                }}
              >
                {item.title}
              </div>
              {/* Metadata badges — grouped so they wrap as a single unit rather
                  than scattering individually across lines. min-w-0 lets the
                  group shrink and wrap its own badges when it's alone on a
                  narrow line. */}
              <div className="flex flex-wrap items-center gap-1 min-w-0">
                {item.status !== 'pending' && (
                  <Badge
                    variant="secondary"
                    className={`text-[10px] px-1.5 py-0 border-0 ${statusStyle.className}`}
                  >
                    {statusStyle.label}
                  </Badge>
                )}
                {item.assignedToPrincipalId && resolvePrincipalName && (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 gap-1 text-muted-foreground"
                    title={`Assigned to ${resolvePrincipalName(item.assignedToPrincipalId)}`}
                  >
                    <UserRound className="h-2.5 w-2.5" />
                    {resolvePrincipalName(item.assignedToPrincipalId)}
                  </Badge>
                )}
                {isInReview && item.reviewAssignedToPrincipalId && resolvePrincipalName && (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 gap-1 border-amber-500/40 text-amber-700 dark:text-amber-400"
                    title={`Review requested from ${resolvePrincipalName(item.reviewAssignedToPrincipalId)}`}
                  >
                    <Eye className="h-2.5 w-2.5" />
                    {resolvePrincipalName(item.reviewAssignedToPrincipalId)}
                  </Badge>
                )}
                {item.roadmapVoteCount ? (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 gap-1 text-muted-foreground"
                    title={`${item.roadmapVoteCount} ${item.roadmapVoteCount === 1 ? 'person has' : 'people have'} requested this`}
                  >
                    <ThumbsUp className="h-2.5 w-2.5" />
                    {item.roadmapVoteCount}
                  </Badge>
                ) : null}
                {item.checklistTotal ? (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 gap-1 text-muted-foreground"
                    title={`${item.checklistDone} of ${item.checklistTotal} checklist items done`}
                  >
                    <ListChecks className="h-2.5 w-2.5" />
                    {item.checklistDone}/{item.checklistTotal}
                  </Badge>
                ) : null}
                {inProjectView
                  ? showSectionPathOnTasks && <SectionPathChip path={sectionPath} />
                  : showProjectOnTasks && project && <ProjectChip project={project} />}
              </div>
              {item.description && (
                <p className="basis-full text-xs text-muted-foreground line-clamp-2">
                  {item.description}
                </p>
              )}
              {/* Review actions live on their own full-width line beneath the
                  title (basis-full forces the wrap). Keeping the "Verify" /
                  "Send back" text labels — they're temporary to the review
                  state and stay scannable — while no longer stealing a fixed
                  horizontal band from the title band on every in-review row. */}
              {reviewMode && isInReview && (isOwner || canReview) && (
                <div className="basis-full flex flex-wrap items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 px-2 text-xs text-green-700 hover:bg-green-100 hover:text-green-800 dark:text-green-400 dark:hover:bg-green-900/30"
                    onClick={() => onApprove?.(item)}
                    title="Verify — mark this item as done"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Verify
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 px-2 text-xs text-muted-foreground"
                    onClick={() => onReject?.(item)}
                    title="Send back to in progress"
                  >
                    <Undo2 className="h-3.5 w-3.5" />
                    Send back
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* "+" — add a task below this one. Regular tasks only; a sub-task can't
            have a task below it (that would land in Unsorted), so it's hidden. */}
        {effectiveCanManageSection && !asSubtask && (
          <Button
            variant="ghost"
            size="icon"
            className={`h-7 w-7 flex-shrink-0 text-muted-foreground/60 hover:text-foreground ${actionVisibility}`}
            onClick={(e) => {
              e.stopPropagation();
              setAddBelowActive(true);
              setTimeout(() => addBelowRef.current?.focus(), 0);
            }}
            aria-label="Add item below"
            title="Add a task below"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        )}

        {canAddSubtask && (
          <Button
            variant="ghost"
            size="icon"
            className={`h-7 w-7 flex-shrink-0 text-muted-foreground/60 hover:text-foreground ${actionVisibility}`}
            onClick={(e) => {
              e.stopPropagation();
              // A parent auto-expands so the new sub-task is visible.
              if (!asSubtask) subtaskCollapse.setCollapsed(false);
              setSubtaskAddActive(true);
              setTimeout(() => subtaskAddRef.current?.focus(), 0);
            }}
            aria-label={
              asSubtask
                ? `Add a sibling sub-task to "${item.title}"`
                : `Add a sub-task to "${item.title}"`
            }
            title={asSubtask ? 'Add a sibling sub-task' : 'Add a sub-task'}
          >
            <ListPlus className="h-3.5 w-3.5" />
          </Button>
        )}

        {/* Public-visibility eye — owner-only, and only on a public list. When the
            section gate already hides this task the eye is a disabled indicator
            (toggling the task wouldn't change its effective visibility); when the
            section is visible it toggles just this task. Muted + hover-reveal
            while visible, always-shown + solid once hidden, so a scan down the
            list surfaces the exceptions. */}
        {showVisibilityEye &&
          (sectionHidden ? (
            <span
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center text-muted-foreground/40"
              title="Hidden from public — this section is hidden"
              aria-label="Hidden from public because its section is hidden"
            >
              <EyeOff className="h-3.5 w-3.5" />
            </span>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className={`h-7 w-7 flex-shrink-0 ${
                item.hiddenFromPublic
                  ? 'text-muted-foreground'
                  : 'text-muted-foreground/50 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100'
              }`}
              onClick={() => onToggleItemHidden?.(item.itemId, !item.hiddenFromPublic)}
              aria-label={
                item.hiddenFromPublic
                  ? `Show "${item.title}" in the public view`
                  : `Hide "${item.title}" from the public view`
              }
              title={
                item.hiddenFromPublic
                  ? 'Hidden from public — click to show'
                  : 'Visible in public view — click to hide'
              }
            >
              {item.hiddenFromPublic ? (
                <EyeOff className="h-3.5 w-3.5" />
              ) : (
                <Eye className="h-3.5 w-3.5" />
              )}
            </Button>
          ))}

        {/* The ⋯ menu. When this row is part of a MULTI-selection it acts on the
            whole selection (bulk verbs, Edit dropped); otherwise the normal
            per-item menu — so a ⋯ always does what the highlight implies. */}
        {(isOwner || effectiveCanEdit || canReview) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 flex-shrink-0"
                aria-label="Item actions"
              >
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {inBulkMode ? (
                <BulkActionsMenuItems {...bulkActions!} />
              ) : (
                <>
                  {(effectiveCanEdit || canReview) && (
                    <DropdownMenuItem onClick={() => onEdit?.(item)}>
                      <Pencil className="h-3.5 w-3.5 mr-2" />
                      Edit
                    </DropdownMenuItem>
                  )}
                  {/* Assign to — for tasks AND sub-tasks. A sub-task defaults to the
                  parent's assignee; reassigning it moves it to that person's My
                  Tasks board. Available to anyone who can edit the task (a member
                  can hand off their own work); the server enforces the same
                  boundary. */}
                  {effectiveCanEdit &&
                    onAssignItem &&
                    assignableMembers &&
                    assignableMembers.length > 0 && (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                          <UserRound className="h-3.5 w-3.5 mr-2" />
                          Assign to
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          <DropdownMenuRadioGroup
                            value={item.assignedToPrincipalId ?? ''}
                            onValueChange={(v) => {
                              if (v === '') onUnassignItem?.(item.itemId);
                              else onAssignItem(item.itemId, v);
                            }}
                          >
                            <DropdownMenuRadioItem value="">Unassigned</DropdownMenuRadioItem>
                            {assignableMembers.map((m) => (
                              <DropdownMenuRadioItem key={m.principalId} value={m.principalId}>
                                {m.label}
                              </DropdownMenuRadioItem>
                            ))}
                          </DropdownMenuRadioGroup>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    )}
                  {effectiveCanEdit && onSetItemStatus && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <CircleDot className="h-3.5 w-3.5 mr-2" />
                        Set status
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        <DropdownMenuRadioGroup
                          value={item.status}
                          onValueChange={(v) => onSetItemStatus(item.itemId, v as TodoItemStatus)}
                        >
                          {statusOptions.map((s) => (
                            <DropdownMenuRadioItem key={s.value} value={s.value}>
                              {s.label}
                            </DropdownMenuRadioItem>
                          ))}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}
                  {/* Set project — a quick reassign without opening the editor. Only on
                  top-level tasks (sub-tasks inherit their parent's project). */}
                  {effectiveCanEdit &&
                    onSetItemProject &&
                    projects &&
                    projects.length > 0 &&
                    !item.parentItemId && (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                          <FolderKanban className="h-3.5 w-3.5 mr-2" />
                          Set project
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          <DropdownMenuRadioGroup
                            value={item.projectId ?? ''}
                            onValueChange={(v) =>
                              onSetItemProject(item.itemId, v === '' ? null : v)
                            }
                          >
                            <DropdownMenuRadioItem value="">No project</DropdownMenuRadioItem>
                            {projects.map((p) => (
                              <DropdownMenuRadioItem key={p.projectId} value={p.projectId}>
                                <span
                                  className={cn(
                                    'mr-2 h-2 w-2 flex-shrink-0 rounded-full',
                                    resolveProjectColor(p.color).dot,
                                  )}
                                />
                                {p.name}
                              </DropdownMenuRadioItem>
                            ))}
                          </DropdownMenuRadioGroup>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    )}
                  {/* Send for review — top-level tasks only (sub-tasks are never
                  independently reviewable). Disabled with a reason until every
                  sub-task + checklist item is done. */}
                  {reviewMode &&
                    effectiveCanEdit &&
                    onRequestReview &&
                    !item.parentItemId &&
                    reviewerTargets &&
                    reviewerTargets.length > 0 &&
                    (reviewBlocked ? (
                      <DropdownMenuItem
                        disabled
                        title={reviewBlockReason}
                        className="text-muted-foreground"
                      >
                        <Eye className="h-3.5 w-3.5 mr-2" />
                        Send for review
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                          <Eye className="h-3.5 w-3.5 mr-2" />
                          Send for review
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          <DropdownMenuItem onClick={() => onRequestReview(item.itemId, null)}>
                            All reviewers
                          </DropdownMenuItem>
                          {reviewerTargets.map((t) => (
                            <DropdownMenuItem
                              key={t.principalId}
                              onClick={() => onRequestReview(item.itemId, t.principalId)}
                            >
                              {t.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    ))}
                  {/* Move to section — a quick re-file. Targets are access-filtered
                  upstream (a member sees only sections they can move into, and no
                  "Unsorted"). Top-level tasks only — sub-tasks live under a parent. */}
                  {(() => {
                    const showUnsorted = (canMoveToUnsorted ?? false) || item.sectionId == null;
                    const hasTargets = (movableSectionOptions?.length ?? 0) > 0 || showUnsorted;
                    return (
                      effectiveCanEdit &&
                      onSetItemSection &&
                      !item.parentItemId &&
                      hasTargets && (
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <FolderInput className="h-3.5 w-3.5 mr-2" />
                            Move to section
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
                            <DropdownMenuRadioGroup
                              value={item.sectionId ?? ''}
                              onValueChange={(v) =>
                                onSetItemSection(item.itemId, v === '' ? null : v)
                              }
                            >
                              {showUnsorted && (
                                <DropdownMenuRadioItem value="">Unsorted</DropdownMenuRadioItem>
                              )}
                              {movableSectionOptions?.map((s) => (
                                <DropdownMenuRadioItem key={s.sectionId} value={s.sectionId}>
                                  <span style={{ paddingLeft: `${s.depth * 0.75}rem` }}>
                                    {s.name}
                                  </span>
                                </DropdownMenuRadioItem>
                              ))}
                            </DropdownMenuRadioGroup>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      )
                    );
                  })()}
                  {/* Move a top-level task (with its sub-tasks) to another list —
                  manager-only; sub-tasks travel with their parent, not alone. */}
                  {isOwner && onMove && !item.parentItemId && (
                    <DropdownMenuItem onClick={() => onMove(item)}>
                      <ArrowRightLeft className="h-3.5 w-3.5 mr-2" />
                      Move to list…
                    </DropdownMenuItem>
                  )}
                  {isOwner && (
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => onDelete?.(item.itemId)}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {addBelowActive && (
        <div className="flex items-center gap-2 px-3 py-2">
          <Input
            ref={addBelowRef}
            value={addBelowTitle}
            onChange={(e) => setAddBelowTitle(e.target.value)}
            placeholder="New item title..."
            className="h-8 text-sm"
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && addBelowTitle.trim()) {
                const title = addBelowTitle.trim();
                setAddBelowActive(false);
                setAddBelowTitle('');
                await onCreateItemBelow?.(item.sectionId, item.itemId, title);
              }
              if (e.key === 'Escape') {
                setAddBelowActive(false);
                setAddBelowTitle('');
              }
            }}
            onBlur={async () => {
              // Save on blur just like pressing Enter (mirrors the section
              // quick-add). Enter/Escape unmount this input before blur
              // dispatches, so there's no double-create on those paths.
              const title = addBelowTitle.trim();
              setAddBelowActive(false);
              setAddBelowTitle('');
              if (title) {
                await onCreateItemBelow?.(item.sectionId, item.itemId, title);
              }
            }}
          />
        </div>
      )}

      {/* "+S" quick-add: a new sub-task (or, on a sub-task row, a sibling). */}
      {subtaskAddActive && (
        <div className={`flex items-center gap-2 py-1 ${asSubtask ? 'px-3' : 'pl-11 pr-3'}`}>
          <Input
            ref={subtaskAddRef}
            value={subtaskAddTitle}
            onChange={(e) => setSubtaskAddTitle(e.target.value)}
            placeholder={asSubtask ? 'New sibling sub-task...' : 'New sub-task...'}
            className="h-8 text-sm"
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && subtaskAddTitle.trim() && subtaskParentId) {
                const title = subtaskAddTitle.trim();
                setSubtaskAddTitle('');
                setSubtaskAddActive(false);
                await onCreateSubtask?.(subtaskParentId, title);
              }
              if (e.key === 'Escape') {
                setSubtaskAddActive(false);
                setSubtaskAddTitle('');
              }
            }}
            onBlur={async () => {
              const title = subtaskAddTitle.trim();
              setSubtaskAddActive(false);
              setSubtaskAddTitle('');
              if (title && subtaskParentId) await onCreateSubtask?.(subtaskParentId, title);
            }}
          />
        </div>
      )}

      {/* Sub-tasks — nested, indented, inert (locked to this parent; not part of
          the list's drag-and-drop). Rendered only for a top-level row. */}
      {!asSubtask && hasSubtasks && subtaskCollapse.expanded && (
        <div className="ml-11 border-l border-border/40 pl-1">
          {childSubtasks.map((st) => (
            <TodoItemRow
              key={st.itemId}
              item={st}
              canEdit={canEdit}
              canManageSection={canManageSection}
              groupContext={groupContext}
              sectionHidden={sectionHidden}
              asSubtask
              parentSectionId={item.sectionId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
